import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Button,
  Snackbar,
  Alert,
  Paper,
  Fade,
  Card,
  CardContent,
  Avatar,
  InputAdornment,
  IconButton,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Checkbox,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormControl,
  Collapse,
} from "@mui/material";
import {
  Payment,
  Description,
  CheckCircle,
  ArrowBack,
  ArrowForward,
  Refresh,
  Send,
  Warning,
  AttachFile,
  Settings,
  CloudUpload,
  Delete,
  Edit,
  Business,
  AccountBalance,
  AccountBalanceWallet as WalletIcon,
  MonetizationOn,
  InfoOutlined,
  ExpandMore,
  ExpandLess,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

// ====== IMPORTS ======
import client from "../../../API";
import API from "../../../API";
import { getPONumber, parseLocalFloat } from "../../../Utils/constants";
import { useCreatorAuth } from "../../../Hooks/creator-use-auth";
import UploadFileDialog from "../../../Components/tools/uploadfile";
import InvoiceSettingsDialog from "../../../Components/tools/CustomInvoiceSettingsDialog";
import {
  showSuccessToast,
  showWarningToast,
  handleApiError
} from "../../../Utils/toastNotifications";

const CreatorInvoicing = () => {
  const navigate = useNavigate();
  const { creatorToken } = useCreatorAuth();
  const username = creatorToken?.creator_user?.username;

  // -----------------------------
  // WIZARD STATE
  // -----------------------------
  const [activeStep, setActiveStep] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const steps = [
    { label: "Payment Setup", icon: <Settings /> },
    { label: "Invoice Details", icon: <Description /> },
    { label: "Documents", icon: <AttachFile /> },
    { label: "Review", icon: <CheckCircle /> },
  ];

  // -----------------------------
  // WALLET STATE
  // -----------------------------
  const [wallets, setWallets] = useState([]);
  const [walletsLoading, setWalletsLoading] = useState(false);
  const [selectedWalletId, setSelectedWalletId] = useState("");
  const [primaryWalletId, setPrimaryWalletId] = useState(null);

  // -----------------------------
  // SETTLEMENT PREFERENCES STATE (SIMPLIFIED)
  // -----------------------------
  const [settlementMethod, setSettlementMethod] = useState("balance"); // Default to balance
  const [showAdvancedSettlement, setShowAdvancedSettlement] = useState(false);
  const [allowBalancePayment, setAllowBalancePayment] = useState(true);

  // -----------------------------
  // INVOICE SETTINGS STATE
  // -----------------------------
  const [invoiceSettings, setInvoiceSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadDialogType, setUploadDialogType] = useState("logo");

  // -----------------------------
  // FORM STATE
  // -----------------------------
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientNotes, setClientNotes] = useState("");
  const [interestRate, setInterestRate] = useState(0);
  const [poNumber, setPONumber] = useState("");
  const [itemDescription, setItemDescription] = useState("");
  const [amountDue, setAmountDue] = useState("");
  const [supportingDocument, setSupportingDocument] = useState("");

  // Email checking state
  const [emailStatus, setEmailStatus] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [clientCompanyName, setClientCompanyName] = useState("");
  const [currentUserCompany, setCurrentUserCompany] = useState("");

  // BlitzPay state
  const [requestBlitzpay, setRequestBlitzpay] = useState(false);

  // -----------------------------
  // SNACKBAR STATE
  // -----------------------------
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const showSnackbar = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };

  const closeSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // -----------------------------
  // CALCULATIONS
  // -----------------------------
  const [blitzFee, netToReceive] = useMemo(() => {
    const amt = parseLocalFloat(amountDue) || 0;
    const rate = Number(interestRate) || 0;
    const fee = (amt * rate) / 100;
    return [fee, Math.max(0, amt - fee)];
  }, [amountDue, interestRate]);

  // Check if client is registered with BlitzPay access
  const hasBlitzPayAccess = emailStatus === 'registered' && interestRate > 0;

  // Determine if wallet selection is needed
  const needsWalletSelection = settlementMethod === 'withdraw' || settlementMethod === 'either';

  // -----------------------------
  // NAVIGATION
  // -----------------------------
  const goToStep = (step) => {
    setIsTransitioning(true);
    setTimeout(() => {
      setActiveStep(step);
      setIsTransitioning(false);
    }, 150);
  };

  const nextStep = () => activeStep < steps.length - 1 && goToStep(activeStep + 1);
  const prevStep = () => activeStep > 0 && goToStep(activeStep - 1);

  // -----------------------------
  // VALIDATION
  // -----------------------------
  const canProceedFromStep = (step) => {
    switch (step) {
      case 0:
        // If settlement method is balance, no wallet needed
        // If withdraw or either, need a wallet selected
        if (needsWalletSelection) {
          return !!selectedWalletId;
        }
        return true; // Balance doesn't need wallet selection
      case 1:
        return !!(clientName && clientEmail && poNumber && amountDue && parseLocalFloat(amountDue) > 0);
      case 2:
        return true;
      case 3:
        return true;
      default:
        return false;
    }
  };

  // -----------------------------
  // WALLET HELPERS
  // -----------------------------
  const getWalletIcon = (walletType) => {
    switch (walletType) {
      case "paypal":
        return <Payment />;
      case "wise":
        return <AccountBalance />;
      case "bank_transfer":
      case "bank_ach":
        return <AccountBalance />;
      case "crypto":
        return <WalletIcon />;
      default:
        return <WalletIcon />;
    }
  };

  const getWalletDisplayDetails = (wallet) => {
    switch (wallet.wallet_type) {
      case "paypal":
        return wallet.paypal_email;
      case "wise":
        return `${wallet.wise_email} (${wallet.wise_currency})`;
      case "bank_transfer":
      case "bank_ach":
        return `${wallet.bank_name || 'Bank'} ${wallet.bank_account_number || ''}`;
      case "crypto":
        return `${wallet.crypto_currency} • ${wallet.crypto_wallet_address?.slice(0, 6)}...${wallet.crypto_wallet_address?.slice(-4)}`;
      default:
        return wallet.nickname || 'Payment Method';
    }
  };

  // -----------------------------
  // INVOICE SETTINGS HANDLERS
  // -----------------------------
  const fetchInvoiceSettings = useCallback(async () => {
    setSettingsLoading(true);
    try {
      const response = await API.invoiceSettings.get();
      if (response.settings) {
        setInvoiceSettings(response.settings);
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error("Error fetching invoice settings:", error);
      }
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  const handleSettingsUpdated = (updatedSettings) => {
    setInvoiceSettings(updatedSettings);
    showSnackbar("Invoice settings saved successfully!", "success");
  };

  // -----------------------------
  // SUPPORTING DOCUMENT HANDLERS
  // -----------------------------
  const handleUploadSuccess = (uploadedFile) => {
    if (uploadedFile && uploadedFile.url) {
      if (uploadDialogType === "logo") {
        // Logo will be handled by the settings dialog
        showSnackbar("Logo uploaded - please save settings", "info");
      } else if (uploadDialogType === "document") {
        setSupportingDocument(uploadedFile.url);
        showSnackbar("Supporting document uploaded successfully", "success");
      }
    }
    setShowUploadDialog(false);
  };

  const handleRemoveDocument = () => {
    setSupportingDocument("");
    showSnackbar("Supporting document removed", "info");
  };

  const handleUploadDialogOpen = (type) => {
    setUploadDialogType(type);
    setShowUploadDialog(true);
  };

  const getFileName = (url) => {
    if (!url) return "";
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const fileName = pathname.split('/').pop();
      return decodeURIComponent(fileName);
    } catch {
      return url.split('/').pop() || "Document";
    }
  };

  // -----------------------------
  // WALLET FETCHING
  // -----------------------------
  const fetchWallets = useCallback(async () => {
    if (!username) return;
    
    setWalletsLoading(true);
    try {
      const response = await API.creatorConnect.getWallets();
      
      if (response?.wallets) {
        setWallets(response.wallets);
        setPrimaryWalletId(response.primary_wallet_id);
        
        // Auto-select primary wallet for advanced options
        if (response.primary_wallet_id && !selectedWalletId) {
          setSelectedWalletId(response.primary_wallet_id);
        }
      }
    } catch (error) {
      console.error("Error fetching wallets:", error);
      showSnackbar("Failed to load payment methods", "error");
    } finally {
      setWalletsLoading(false);
    }
  }, [username, selectedWalletId]);

  // -----------------------------
  // INITIALIZATION
  // -----------------------------
  useEffect(() => {
    if (!username) {
      showSnackbar("Creator authentication required", "error");
      setTimeout(() => navigate("/creatorconnect/login"), 2000);
      return;
    }

    setSelectedCreator({
      creator: username,
      label: username,
    });

    fetchInvoiceSettings();
    fetchWallets();
  }, [username, navigate, fetchInvoiceSettings, fetchWallets]);

  useEffect(() => {
    setPONumber(getPONumber("TCC"));
  }, []);

  useEffect(() => {
    const fetchCurrentUserCompany = async () => {
      if (username) {
        try {
          const response = await API.creatorConnect.fetchUserInfo(username);
          if (response?.found && response.user?.company_name) {
            setCurrentUserCompany(response.user.company_name);
          }
        } catch (error) {
          console.error("Error fetching creator company:", error);
        }
      }
    };
    fetchCurrentUserCompany();
  }, [username]);

  // -----------------------------
  // EMAIL CHECKING
  // -----------------------------
  const handleCheckClientEmail = useCallback(async () => {
    if (!clientEmail?.trim()) return;

    const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clientEmail);
    if (!isValid) {
      setEmailStatus('invalid');
      setInterestRate(0);
      setClientCompanyName("");
      setRequestBlitzpay(false);
      return;
    }

    if (clientEmail.toLowerCase() === username?.toLowerCase()) {
      setEmailStatus('self-invoice');
      setInterestRate(0);
      setClientCompanyName("");
      setRequestBlitzpay(false);
      showSnackbar("You cannot create an invoice for yourself.", "error");
      return;
    }

    setEmailLoading(true);
    setEmailStatus('checking');

    try {
      const response = await API.creatorConnect.fetchUserInfo(clientEmail);
      if (response?.found) {
        const userData = response.user;
        const displayName = [userData.first_name, userData.last_name]
          .filter(Boolean)
          .join(' ')
          .trim();
        
        if (userData.company_name && currentUserCompany && 
            userData.company_name.toLowerCase() === currentUserCompany.toLowerCase()) {
          setEmailStatus('same-company');
          setInterestRate(0);
          setClientCompanyName(userData.company_name);
          setRequestBlitzpay(false);
          showSnackbar(`Cannot invoice ${userData.company_name} team members.`, "error");
          return;
        }
        
        if (displayName) {
          setClientName(displayName);
        }
        setInterestRate(userData.company_interest_rate ?? 10);
        setClientCompanyName(userData.company_name || "");
        setEmailStatus('registered');
        setRequestBlitzpay(userData.company_interest_rate > 0);
        showSnackbar("Client found - BlitzPay available!", "success");
      } else {
        setInterestRate(10);
        setEmailStatus('new-user');
        setClientCompanyName("");
        setRequestBlitzpay(false);
        showSnackbar("New client - standard invoice only.", "info");
      }
    } catch (err) {
      console.error("Error checking email:", err);
      setEmailStatus('error');
      setInterestRate(0);
      setClientCompanyName("");
      setRequestBlitzpay(false);
      showSnackbar(`Error checking client email: ${err.message}`, "error");
    } finally {
      setEmailLoading(false);
    }
  }, [clientEmail, username, currentUserCompany]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (clientEmail) {
        handleCheckClientEmail();
      } else {
        setEmailStatus(null);
        setInterestRate(0);
        setClientCompanyName("");
        setRequestBlitzpay(false);
      }
    }, 600);

    return () => clearTimeout(timer);
  }, [clientEmail, handleCheckClientEmail]);

  // -----------------------------
  // HANDLERS
  // -----------------------------
  const handleNext = () => {
    if (!canProceedFromStep(activeStep)) {
      const stepErrors = {
        0: needsWalletSelection 
          ? "Please select a payment method to continue." 
          : "Please configure your payment settings.",
        1: "Please complete all required fields: Client name, email, PO number, and amount.",
      };
      showSnackbar(stepErrors[activeStep] || "Please complete all required fields.", "error");
      return;
    }

    if (activeStep === 1) {
      if (emailStatus === 'self-invoice' || emailStatus === 'same-company') {
        showSnackbar("Please use a different client email address.", "error");
        return;
      }
    }

    nextStep();
  };

  const generatePO = useCallback(() => {
    setPONumber(getPONumber("TCC"));
  }, []);

  // -----------------------------
  // SUBMIT HANDLERS
  // -----------------------------
  const handleSubmitInvoice = async () => {
    // For balance settlement, we'll use primary wallet or first available
    let walletForSubmission = selectedWalletId;
    
    if (!needsWalletSelection) {
      // Use primary wallet or first wallet for balance settlement
      walletForSubmission = primaryWalletId || wallets[0]?.id;
    }

    const selectedWallet = wallets.find(w => w.id === walletForSubmission);
    if (!selectedWallet) {
      showSnackbar("No payment method available", "error");
      return;
    }

    setIsLoading(true);
    const submissionData = {
      creator_id: selectedCreator?.creator || username,
      payment_method: selectedWallet.wallet_type,
      wallet_id: walletForSubmission,
      po_number: poNumber,
      amount_due: parseLocalFloat(amountDue),
      email: clientEmail,
      client_name: clientName,
      client_email: clientEmail,
      client_notes: clientNotes,
      request_blitzpay: requestBlitzpay,
      supporting_document: supportingDocument || null,
      preferred_settlement_method: settlementMethod,
      allow_balance_payment: allowBalancePayment,
      settlement_wallet_id: walletForSubmission,
      items: [
        {
          description: itemDescription,
          amount: parseLocalFloat(amountDue),
        },
      ],
    };

    try {
      const response = await client.invoices.create(submissionData);

      if (response.status === "Success") {
        if (response.requires_payment_method) {
          showWarningToast(
            "Payment Method Required",
            {
              description: "Invoice created! Please add a payment method to receive payment.",
              duration: 8000
            }
          );
          setTimeout(() => {
            navigate("/creatorconnect/edit?tab=wallets");
          }, 2000);
        }
        else if (response.withdrawal_created) {
          showSuccessToast(
            "Invoice Created & Withdrawal Initiated!",
            {
              description: `Your payment will be processed automatically. Withdrawal ID: ${response.withdrawal_id}`,
              duration: 6000
            }
          );
          setTimeout(() => {
            navigate("/creatorconnect/payouts-invoicing");
          }, 1500);
        }
        else {
          showSuccessToast("Invoice created and shared successfully!");
          setTimeout(() => {
            navigate("/creatorconnect/payouts-invoicing");
          }, 1500);
        }
      } else {
        throw new Error(response.message || "Error creating invoice");
      }
    } catch (error) {
      console.error("Error submitting invoice:", error);
      handleApiError(error, "Error submitting invoice");
    } finally {
      setIsLoading(false);
    }
  };

  // -----------------------------
  // EMAIL STATUS RENDERER
  // -----------------------------
  const renderEmailStatus = () => {
    switch (emailStatus) {
      case 'checking':
        return (
          <Alert severity="info" icon={<CircularProgress size={16} />} sx={{ mt: 2 }}>
            Verifying client information...
          </Alert>
        );
      case 'registered':
        return (
          <Alert severity="success" sx={{ mt: 2 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <CheckCircle fontSize="small" />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Client verified with BlitzPay access
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                {clientCompanyName && `${clientCompanyName} • `}
                {interestRate}% platform fee • Instant payment available
              </Typography>
            </Box>
          </Alert>
        );
      case 'new-user':
        return (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              New client - Standard invoice
            </Typography>
            <Typography variant="caption" color="text.secondary">
              They'll receive an email to view and pay the invoice. BlitzPay is not available for new clients.
            </Typography>
          </Alert>
        );
      case 'self-invoice':
        return (
          <Alert severity="error" sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Warning fontSize="small" />
              You cannot create an invoice for yourself
            </Box>
          </Alert>
        );
      case 'same-company':
        return (
          <Alert severity="error" sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Warning fontSize="small" />
              Cannot invoice team members from {clientCompanyName}
            </Box>
          </Alert>
        );
      case 'invalid':
        return (
          <Alert severity="error" sx={{ mt: 2 }}>
            Please enter a valid email address
          </Alert>
        );
      case 'error':
        return (
          <Alert severity="error" sx={{ mt: 2 }}>
            Unable to verify client — please try again
          </Alert>
        );
      default:
        return null;
    }
  };

  // -----------------------------
  // STEP CONTENT RENDERER
  // -----------------------------
  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        // Payment Setup
        return (
          <Fade in={!isTransitioning} timeout={300}>
            <Box>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 80, height: 80, mx: 'auto', mb: 2 }}>
                  <Settings sx={{ fontSize: 40 }} />
                </Avatar>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  Payment Setup
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Configure how you'll receive payment for this invoice
                </Typography>
              </Box>

              <Grid container spacing={3}>
                {/* Company Info */}
                <Grid item xs={12}>
                  <Card variant="outlined" sx={{ p: 3, borderRadius: 2, bgcolor: 'grey.50' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                          Invoicing as
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5 }}>
                          {invoiceSettings?.invoice_name || username}
                        </Typography>
                        {invoiceSettings && (
                          <Typography variant="caption" color="text.secondary">
                            {invoiceSettings.email && `${invoiceSettings.email}`}
                            {invoiceSettings.phone_number && ` • ${invoiceSettings.phone_number}`}
                          </Typography>
                        )}
                      </Box>
                      <Button
                        startIcon={invoiceSettings ? <Edit /> : <Business />}
                        onClick={() => setShowSettingsDialog(true)}
                        variant={invoiceSettings ? "outlined" : "contained"}
                        size="small"
                      >
                        {invoiceSettings ? "Edit" : "Add Settings"}
                      </Button>
                    </Box>
                  </Card>
                </Grid>

                {/* Settlement Preferences - Simplified */}
                <Grid item xs={12}>
                  <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: 'success.50', border: '2px solid', borderColor: 'success.200' }}>
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                        <MonetizationOn color="success" sx={{ fontSize: 28 }} />
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            Payment Settlement
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            How you'll receive payment when invoice is approved
                          </Typography>
                        </Box>
                      </Box>

                      <Alert severity="success" sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          ✓ Default: Add to Blitz Balance
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Funds will be added to your Blitz balance. You can withdraw anytime.
                        </Typography>
                      </Alert>

                      <Button
                        size="small"
                        onClick={() => setShowAdvancedSettlement(!showAdvancedSettlement)}
                        endIcon={showAdvancedSettlement ? <ExpandLess /> : <ExpandMore />}
                        sx={{ textTransform: 'none' }}
                      >
                        Advanced settlement options
                      </Button>

                      <Collapse in={showAdvancedSettlement}>
                        <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                          <FormControl component="fieldset" sx={{ width: '100%', mb: 3 }}>
                            <RadioGroup
                              value={settlementMethod}
                              onChange={(e) => setSettlementMethod(e.target.value)}
                            >
                              <FormControlLabel
                                value="balance"
                                control={<Radio size="small" />}
                                label={
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      Add to Blitz Balance (Recommended)
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      Maximum flexibility - withdraw when you want
                                    </Typography>
                                  </Box>
                                }
                                sx={{ mb: 1 }}
                              />
                              <FormControlLabel
                                value="withdraw"
                                control={<Radio size="small" />}
                                label={
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      Direct Withdrawal
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      Automatically send to selected payment method
                                    </Typography>
                                  </Box>
                                }
                                sx={{ mb: 1 }}
                              />
                              <FormControlLabel
                                value="either"
                                control={<Radio size="small" />}
                                label={
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      Choose Later
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      Decide when invoice is approved
                                    </Typography>
                                  </Box>
                                }
                              />
                            </RadioGroup>
                          </FormControl>

                          {/* Wallet Selection - Only show when withdraw or either */}
                          {needsWalletSelection && (
                            <Box sx={{ mb: 3 }}>
                              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                                Select Withdrawal Method
                              </Typography>
                              {walletsLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
                                  <CircularProgress size={24} />
                                  <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                                    Loading payment methods...
                                  </Typography>
                                </Box>
                              ) : wallets.length === 0 ? (
                                <Alert severity="warning">
                                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                    No payment methods found
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                                    Add a payment method to enable direct withdrawal
                                  </Typography>
                                  <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() => navigate("/creatorconnect/edit?tab=wallets")}
                                    sx={{ mt: 1 }}
                                  >
                                    Add Payment Method
                                  </Button>
                                </Alert>
                              ) : (
                                <RadioGroup
                                  value={selectedWalletId}
                                  onChange={(e) => setSelectedWalletId(e.target.value)}
                                >
                                  {wallets.map((wallet) => (
                                    <Paper
                                      key={wallet.id}
                                      elevation={selectedWalletId === wallet.id ? 3 : 0}
                                      sx={{
                                        p: 2,
                                        mb: 1.5,
                                        border: '2px solid',
                                        borderColor: selectedWalletId === wallet.id ? 'primary.main' : 'divider',
                                        borderRadius: 2,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        bgcolor: selectedWalletId === wallet.id ? 'primary.50' : 'transparent',
                                        '&:hover': {
                                          borderColor: 'primary.main',
                                          boxShadow: 1,
                                        },
                                      }}
                                      onClick={() => setSelectedWalletId(wallet.id)}
                                    >
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Radio
                                          checked={selectedWalletId === wallet.id}
                                          size="small"
                                          sx={{ p: 0 }}
                                        />
                                        
                                        <Avatar
                                          sx={{
                                            bgcolor: selectedWalletId === wallet.id ? 'primary.main' : 'grey.200',
                                            color: selectedWalletId === wallet.id ? 'white' : 'text.secondary',
                                            width: 40,
                                            height: 40,
                                          }}
                                        >
                                          {getWalletIcon(wallet.wallet_type)}
                                        </Avatar>

                                        <Box sx={{ flex: 1 }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                              {wallet.nickname || wallet.wallet_type.replace('_', ' ').toUpperCase()}
                                            </Typography>
                                            {wallet.id === primaryWalletId && (
                                              <Chip
                                                label="Primary"
                                                size="small"
                                                color="primary"
                                                sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }}
                                              />
                                            )}
                                          </Box>
                                          <Typography variant="caption" color="text.secondary">
                                            {getWalletDisplayDetails(wallet)}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    </Paper>
                                  ))}
                                </RadioGroup>
                              )}
                            </Box>
                          )}

                          <Divider sx={{ my: 2 }} />

                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={allowBalancePayment}
                                onChange={(e) => setAllowBalancePayment(e.target.checked)}
                                color="success"
                              />
                            }
                            label={
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  Allow client to pay from Blitz Balance
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Enables instant payment if client has sufficient balance
                                </Typography>
                              </Box>
                            }
                          />
                        </Box>
                      </Collapse>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          </Fade>
        );

      case 1:
        // Invoice Details - keeping same as before
        return (
          <Fade in={!isTransitioning} timeout={300}>
            <Box>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Avatar sx={{ bgcolor: 'info.main', width: 80, height: 80, mx: 'auto', mb: 2 }}>
                  <Description sx={{ fontSize: 40 }} />
                </Avatar>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  Invoice Details
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Who are you invoicing and for how much?
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                    Client Information
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="Client Email Address"
                    type="email"
                    value={clientEmail}
                    onChange={(e) => setClientEmail(e.target.value)}
                    fullWidth
                    autoFocus
                    required
                    error={emailStatus === 'self-invoice' || emailStatus === 'same-company' || emailStatus === 'invalid'}
                    InputProps={{
                      endAdornment: emailLoading && (
                        <InputAdornment position="end">
                          <CircularProgress size={20} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  {renderEmailStatus()}
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="Client Full Name"
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    fullWidth
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Additional Notes (Optional)"
                    value={clientNotes}
                    onChange={(e) => setClientNotes(e.target.value)}
                    fullWidth
                    multiline
                    rows={2}
                    placeholder="Add any context about the client or project..."
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                    Invoice Details
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <TextField
                      label="PO Number"
                      value={poNumber}
                      onChange={(e) => setPONumber(e.target.value)}
                      fullWidth
                      required
                    />
                    <IconButton
                      onClick={generatePO}
                      sx={{ mt: 1, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}
                      title="Generate new PO number"
                    >
                      <Refresh />
                    </IconButton>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="Invoice Amount"
                    type="number"
                    value={amountDue}
                    onChange={(e) => setAmountDue(e.target.value)}
                    fullWidth
                    required
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Description of Work"
                    multiline
                    rows={4}
                    value={itemDescription}
                    onChange={(e) => setItemDescription(e.target.value)}
                    fullWidth
                    placeholder="Describe the work, product, or service being invoiced..."
                  />
                </Grid>

                {hasBlitzPayAccess && (
                  <Grid item xs={12}>
                    <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: 'primary.50', border: '2px solid', borderColor: 'primary.200' }}>
                      <CardContent>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={requestBlitzpay}
                              onChange={(e) => setRequestBlitzpay(e.target.checked)}
                              color="primary"
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body1" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                                Request BlitzPay Approval
                                <Chip label="Instant Payment" size="small" color="primary" />
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {clientCompanyName} has BlitzPay access. When approved, you'll receive payment instantly minus {interestRate}% platform fee.
                              </Typography>
                            </Box>
                          }
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {emailStatus === 'new-user' && (
                  <Grid item xs={12}>
                    <Alert severity="info" icon={<InfoOutlined />}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        BlitzPay not available for new clients
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        BlitzPay instant payment is only available for companies registered on our platform with enterprise agreements. This client will receive a standard invoice.
                      </Typography>
                    </Alert>
                  </Grid>
                )}

                {amountDue && hasBlitzPayAccess && requestBlitzpay && (
                  <Grid item xs={12}>
                    <Card variant="outlined" sx={{ borderRadius: 2, border: '2px solid', borderColor: 'success.300' }}>
                      <CardContent>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, textAlign: 'center' }}>
                          Payment Breakdown
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, py: 1 }}>
                          <Typography variant="body1">Invoice Amount:</Typography>
                          <Typography variant="body1" sx={{ fontWeight: 700 }}>
                            ${parseLocalFloat(amountDue).toFixed(2)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, py: 1, bgcolor: 'error.50', px: 2, borderRadius: 1 }}>
                          <Typography variant="body2">Platform Fee ({interestRate}%):</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main' }}>
                            -${blitzFee.toFixed(2)}
                          </Typography>
                        </Box>
                        <Divider sx={{ my: 2 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', bgcolor: 'success.50', p: 2, borderRadius: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            You'll Receive:
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
                            ${netToReceive.toFixed(2)}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Fade>
        );

      case 2:
        // Supporting Documents - keeping same
        return (
          <Fade in={!isTransitioning} timeout={300}>
            <Box>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Avatar sx={{ bgcolor: 'warning.main', width: 80, height: 80, mx: 'auto', mb: 2 }}>
                  <AttachFile sx={{ fontSize: 40 }} />
                </Avatar>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  Supporting Documents
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Optionally attach documents (contracts, receipts, etc.)
                </Typography>
              </Box>

              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  This step is optional
                </Typography>
                <Typography variant="caption">
                  Supporting documents can help speed up approval but aren't required
                </Typography>
              </Alert>

              <Paper
                elevation={0}
                sx={{
                  p: 5,
                  border: "2px dashed",
                  borderColor: supportingDocument ? "success.main" : "grey.300",
                  borderRadius: 3,
                  textAlign: "center",
                  backgroundColor: supportingDocument ? "success.50" : "grey.50",
                  transition: "all 0.3s ease",
                }}
              >
                {supportingDocument ? (
                  <Box>
                    <CheckCircle
                      sx={{ fontSize: 72, color: "success.main", mb: 2 }}
                    />
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
                      Document Attached
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1.5,
                        mb: 3,
                        p: 2,
                        bgcolor: 'white',
                        borderRadius: 2,
                        maxWidth: 400,
                        mx: 'auto',
                      }}
                    >
                      <Description color="action" sx={{ fontSize: 32 }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {getFileName(supportingDocument)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<Delete />}
                        onClick={handleRemoveDocument}
                        size="large"
                      >
                        Remove
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<CloudUpload />}
                        onClick={() => handleUploadDialogOpen("document")}
                        size="large"
                      >
                        Replace
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box>
                    <CloudUpload
                      sx={{ fontSize: 80, color: "grey.400", mb: 2 }}
                    />
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
                      No Document Attached
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 4, maxWidth: 500, mx: 'auto' }}>
                      Upload a PDF, image, or other document to support your invoice (contracts, receipts, work samples, etc.)
                    </Typography>
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<CloudUpload />}
                      onClick={() => handleUploadDialogOpen("document")}
                      sx={{ px: 4, py: 1.5 }}
                    >
                      Upload Document
                    </Button>
                  </Box>
                )}
              </Paper>
            </Box>
          </Fade>
        );

      case 3:
        // Review & Submit - keeping same structure, showing wallet only if needed
        return (
          <Fade in={!isTransitioning} timeout={300}>
            <Box>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Avatar sx={{ bgcolor: 'success.main', width: 80, height: 80, mx: 'auto', mb: 2 }}>
                  <CheckCircle sx={{ fontSize: 40 }} />
                </Avatar>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  Review & Submit
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Double-check everything before sending
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Card variant="outlined" sx={{ borderRadius: 2, border: '2px solid', borderColor: 'primary.200' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2.5, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Settings color="primary" />
                      Payment Setup
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                          Invoicing As
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>
                          {invoiceSettings?.invoice_name || username}
                        </Typography>
                      </Grid>
                      {needsWalletSelection && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                            Payment Method
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>
                            {wallets.find(w => w.id === selectedWalletId)?.nickname || 
                             wallets.find(w => w.id === selectedWalletId)?.wallet_type}
                          </Typography>
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                          <MonetizationOn color="success" fontSize="small" />
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Settlement: 
                          </Typography>
                          <Chip 
                            label={
                              settlementMethod === 'balance' ? 'Add to Blitz Balance' :
                              settlementMethod === 'withdraw' ? 'Direct Withdrawal' :
                              'Choose When Approved'
                            }
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        </Box>
                        {allowBalancePayment && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            ✓ Client can pay from their Blitz balance
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                <Card variant="outlined" sx={{ borderRadius: 2, border: '2px solid', borderColor: 'info.200' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2.5, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Description color="info" />
                      Invoice Details
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                          Client
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>
                          {clientName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {clientEmail}
                        </Typography>
                        {clientCompanyName && (
                          <Chip label={clientCompanyName} size="small" sx={{ mt: 1 }} />
                        )}
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                          Amount & PO
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5, color: 'primary.main' }}>
                          ${parseLocalFloat(amountDue).toFixed(2)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          PO: {poNumber}
                        </Typography>
                      </Grid>
                      {itemDescription && (
                        <Grid item xs={12}>
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                            Description
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {itemDescription}
                          </Typography>
                        </Grid>
                      )}
                      {requestBlitzpay && (
                        <Grid item xs={12}>
                          <Alert severity="success" sx={{ mt: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              ✓ BlitzPay Approval Requested
                            </Typography>
                            <Typography variant="caption">
                              Client will be asked to approve instant payment via BlitzPay
                            </Typography>
                          </Alert>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>

                {supportingDocument && (
                  <Card variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AttachFile />
                        Supporting Document
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Description color="action" sx={{ fontSize: 32 }} />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {getFileName(supportingDocument)}
                        </Typography>
                        <Chip label="Attached" color="success" size="small" />
                      </Box>
                    </CardContent>
                  </Card>
                )}

                {hasBlitzPayAccess && requestBlitzpay && (
                  <Card 
                    variant="outlined" 
                    sx={{ 
                      borderRadius: 2,
                      border: '2px solid',
                      borderColor: 'success.300',
                      background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h6" sx={{ mb: 2.5, fontWeight: 700 }}>
                        Final Payment Breakdown
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, py: 1 }}>
                        <Typography variant="body1">Invoice Amount:</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 700 }}>
                          ${parseLocalFloat(amountDue).toFixed(2)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, py: 1 }}>
                        <Typography variant="body2">BlitzPay Fee ({interestRate}%):</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main' }}>
                          -${blitzFee.toFixed(2)}
                        </Typography>
                      </Box>
                      <Divider sx={{ my: 2 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                          You'll Receive:
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>
                          ${netToReceive.toFixed(2)}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                )}
              </Box>
            </Box>
          </Fade>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: { xs: 2, md: 4 } }}>
      <Box sx={{ textAlign: 'center', mb: 5 }}>
        <Typography variant="h3" sx={{ fontWeight: 800, mb: 1 }}>
          Create Invoice
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Get paid faster with professional invoicing
        </Typography>
      </Box>

      <Stepper activeStep={activeStep} sx={{ mb: 5 }}>
        {steps.map((step, index) => (
          <Step key={step.label}>
            <StepLabel 
              StepIconComponent={() => (
                <Avatar 
                  sx={{ 
                    width: 40, 
                    height: 40,
                    bgcolor: index < activeStep ? 'success.main' : index === activeStep ? 'primary.main' : 'grey.300',
                    color: index <= activeStep ? 'white' : 'grey.600',
                    fontSize: 20,
                  }}
                >
                  {index < activeStep ? <CheckCircle /> : step.icon}
                </Avatar>
              )}
            >
              <Typography sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                {step.label}
              </Typography>
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      <Paper 
        elevation={0} 
        sx={{ 
          p: { xs: 3, md: 5 }, 
          borderRadius: 3, 
          minHeight: 500,
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        }}
      >
        {renderStepContent()}
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={prevStep}
          disabled={activeStep === 0 || isLoading}
          size="large"
          sx={{ minWidth: 120 }}
        >
          Back
        </Button>

        {activeStep < steps.length - 1 ? (
          <Button
            variant="contained"
            size="large"
            endIcon={<ArrowForward />}
            onClick={handleNext}
            disabled={
              !canProceedFromStep(activeStep) || 
              isLoading || 
              emailLoading ||
              emailStatus === 'self-invoice' ||
              emailStatus === 'same-company'
            }
            sx={{ minWidth: 120 }}
          >
            Next Step
          </Button>
        ) : (
          <Button
            variant="contained"
            size="large"
            color="success"
            onClick={handleSubmitInvoice}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : <Send />}
            sx={{ minWidth: 180, fontWeight: 700 }}
          >
            {isLoading ? 'Submitting...' : 'Submit Invoice'}
          </Button>
        )}
      </Box>

      <InvoiceSettingsDialog
        open={showSettingsDialog}
        onClose={() => setShowSettingsDialog(false)}
        onSettingsUpdated={handleSettingsUpdated}
        onUploadLogo={() => handleUploadDialogOpen("logo")}
      />

      <UploadFileDialog 
        open={showUploadDialog} 
        onClose={() => setShowUploadDialog(false)}
        onUploadSuccess={handleUploadSuccess}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CreatorInvoicing;